import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConfirmDialog from "@/components/ConfirmDialog";
import { WRITE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, fmtBR, formatApiError, WEEKDAY_NAMES } from "@/lib/api";

const SCOPE_LABELS = { nacional: "Nacional", estadual: "Estadual (SP)", municipal: "Municipal (Cabreúva)", outro: "Outro" };

export default function Holidays() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user.role);
  const [year, setYear] = useState("2026");
  const [holidays, setHolidays] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", scope: "nacional" });
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    const { data } = await api.get("/holidays", { params: { year: Number(year) } });
    setHolidays(data);
  };

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/holidays", form);
      toast.success("Feriado cadastrado.");
      setDialogOpen(false);
      setForm({ date: "", name: "", scope: "nacional" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const toggleException = async (h) => {
    try {
      await api.put(`/holidays/${h.id}/exception`, { has_exception: !h.has_exception });
      toast.success(!h.has_exception ? "Exceção criada: aulas permitidas nesta data." : "Exceção removida.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/holidays/${deleting.id}`);
      toast.success("Feriado removido.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Feriados</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Não há aulas em feriados. Ative a exceção para permitir aulas em uma data específica.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28" data-testid="holiday-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["2025", "2026", "2027"].map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canWrite && (
            <Button onClick={() => { setError(""); setDialogOpen(true); }} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="holiday-add-button">
              <Plus size={16} className="mr-2" /> Novo feriado
            </Button>
          )}
        </div>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Data</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Feriado</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Escopo</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Exceção (permite aulas)</TableHead>
              {canWrite && <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {holidays.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="p-6 text-sm text-muted-foreground" data-testid="holidays-empty">
                  Nenhum feriado cadastrado neste ano.
                </TableCell>
              </TableRow>
            )}
            {holidays.map((h) => {
              const weekday = new Date(`${h.date}T12:00:00`).getDay();
              return (
                <TableRow key={h.id} className="transition-colors hover:bg-zinc-50" data-testid={`holiday-row-${h.date}`}>
                  <TableCell className="font-medium">
                    {fmtBR(h.date)}
                    <span className="ml-2 text-xs text-muted-foreground">{WEEKDAY_NAMES[(weekday + 6) % 7]}</span>
                  </TableCell>
                  <TableCell>{h.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{SCOPE_LABELS[h.scope] || h.scope}</Badge>
                  </TableCell>
                  <TableCell>
                    {canWrite ? (
                      <Switch checked={h.has_exception} onCheckedChange={() => toggleException(h)} data-testid={`holiday-exception-${h.date}`} />
                    ) : (
                      <Badge variant={h.has_exception ? "default" : "secondary"} className={h.has_exception ? "bg-primary text-primary-foreground" : ""}>
                        {h.has_exception ? "Sim" : "Não"}
                      </Badge>
                    )}
                  </TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setDeleting(h)} data-testid={`holiday-delete-${h.date}`}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>Novo feriado</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="holiday-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Aniversário de Cabreúva" data-testid="holiday-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Escopo</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="holiday-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">Nacional</SelectItem>
                  <SelectItem value="estadual">Estadual (SP)</SelectItem>
                  <SelectItem value="municipal">Municipal (Cabreúva)</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="holiday-save-button">
              Salvar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Remover feriado"
        description={`Remover "${deleting?.name}" (${deleting ? fmtBR(deleting.date) : ""})?`}
        onConfirm={remove}
        testId="holiday-delete-confirm"
      />
    </div>
  );
}
